import Head from "next/head";
import Link from "next/link";
import dynamic from "next/dynamic";
import { getRandom } from "../../utils/blogUtils";
import blogData from "../../blogs.json";
import Service2 from "../../components/services/Service2";
const Capabilities2 = dynamic(() => import("../../components/services/Capabilities2"), { ssr: false });
import SuccessStoryEmbed from "../../components/trust/SuccessStoryEmbed";
import ServiceFAQ from "../../components/services/ServiceFAQ";
import ServiceEngagementLinks from "../../components/services/ServiceEngagementLinks";
import { CustomerSection } from "../../components/ai-agent-demo/CustomerSection";
const Footer = dynamic(() => import("../../components/Footer"), { ssr: false });

forwardDeployedEngineering.title = "Forward Deployed Engineering for Enterprise AI | ITMTB";
forwardDeployedEngineering.desc =
  "FDE-led deployment: senior engineers embedded with your teams to move a difficult AI use case from workflow discovery to secure production — not a proof of concept, not staff augmentation. Starts with a fixed-scope AI Deployment Readiness Sprint.";
forwardDeployedEngineering.header_img = "https://www.itmtb.com/services/capability/data.webp";
forwardDeployedEngineering.keywords =
  "forward deployed engineering, forward deployed engineer India, enterprise AI deployment India, embedded AI engineers, AI production deployment partner, agentic AI deployment, AI deployment readiness sprint, fractional CTO for AI, forward deployed AI engineering pod, AI use case to production India";

const faqItems = [
  {
    q: "What is forward deployed engineering (FDE)?",
    a: "Forward deployed engineering is a delivery model where senior engineers work embedded alongside your business and technology teams — inside your operating context and, where required, inside your environment — to take a difficult problem from discovery all the way to a working production system. The term became prominent through firms like Palantir and is now used across enterprise AI, where OpenAI and Anthropic describe forward-deployed engineers who scope complex customer systems, write production-grade code, and keep a deployment moving through direct customer collaboration. ITMTB uses the same model specifically for enterprise AI: rather than handing over a strategy deck or a demo, an FDE engagement owns a defined operational outcome and concludes with a production workflow, evidence, documentation, and a handover or managed-operations plan.",
  },
  {
    q: "How is FDE different from staff augmentation or a body shop?",
    a: "Staff augmentation supplies people by the hour who work under your direction and own no outcome — if the project stalls, that is your problem to solve. A forward-deployed engagement is the opposite: it owns a defined operational result. We scope a specific workflow, agree success criteria and failure boundaries up front, and the engagement is complete when that workflow works in production — not when a timesheet is filled. You are buying an outcome and a small senior team that stays close to your users until the outcome is real, not a headcount top-up.",
  },
  {
    q: "How is this different from AI consulting or a proof of concept?",
    a: "AI consulting typically produces advice — a strategy, an opportunity map, a recommendation — and stops before implementation. A proof of concept produces a demo that works in a controlled setting but rarely survives contact with real data, real permissions, and real users. Forward deployed engineering is built to cross exactly that gap: the same team that scopes the use case also builds it, integrates it with your systems, adds the evaluations and audit trails production requires, deploys it into your environment, and works alongside real users until it holds up. The output is a running system measured on business results, not demo quality.",
  },
  {
    q: "How large is ITMTB's forward-deployed engineering team?",
    a: "We deliver forward-deployed engagements as a small, senior pod — typically a technical lead or architect, one or two engineers, and security or platform input when the deployment requires it — working closely with a workflow or domain specialist from your side. This is deliberate. A forward-deployed engagement depends on a few people who deeply understand your problem and stay with it, not a large bench of interchangeable contractors. Where an engagement needs to scale, it draws on ITMTB's wider engineering, cloud, security, and managed-services teams rather than diluting the embedded pod.",
  },
  {
    q: "What is the AI Deployment Readiness Sprint and what do I get?",
    a: "The AI Deployment Readiness Sprint is the fixed-scope, fixed-price entry point — the discovery step every ITMTB engagement starts with, focused on a specific AI use case. Over roughly two weeks we observe the actual operating process, identify the users, decisions, systems, and exceptions involved, assess data and integration readiness, and define where AI can create measurable value. You leave with a workflow map, a use-case definition with success criteria and failure boundaries, a data and integration assessment, a security-constraints view, an evaluation plan, a target architecture, a deployment roadmap, a delivery estimate, and a risk register. It is a low-risk way to find out whether a use case is worth building — and if discovery says it is not, you can stop there.",
  },
  {
    q: "How long does a forward-deployed AI engagement take, and where do the engineers work?",
    a: "A readiness sprint runs about two weeks. A forward-deployed AI sprint that takes a scoped use case to production typically runs six to twelve weeks depending on integration complexity, data readiness, and the number of human-approval points. The engineers work close to your teams — remotely, on-site, or hybrid — and deploy into a customer-controlled environment. For organisations that cannot send data to third-party services, we support private cloud and on-premises deployment. The defining feature is proximity: we work where the workflow actually happens, alongside the people who use it.",
  },
  {
    q: "How does forward deployed engineering relate to Fractional CTO / technology leadership?",
    a: "They sit at different points of the same chain. Fractional Technology Leadership decides what should be built, changed, or governed — the judgment layer: portfolio priorities, architecture governance, vendor decisions, risk control, and executive reporting. Forward deployed engineering makes a chosen use case operational. Managed AI Operations runs and improves it after launch. Many engagements use only one layer; where an organisation faces multiple AI decisions rather than one bounded deployment, the leadership layer sets direction and the forward-deployed pod executes against it. ITMTB's fractional technology leadership engagements are led by Aakash Ahuja, Founder and Director of Technology and Cybersecurity.",
  },
  {
    q: "Who owns the system after the deployment is live?",
    a: "You do — that is the point of the model. Every engagement is built toward a clean handover: documentation, runbooks, evaluation and regression suites, observability, and team training so your people can own and extend the system. Where you would rather not run it in-house, the same team can continue as Managed AI Operations — monitoring, incident handling, evaluation regression, cost tracking, prompt and model updates, security review, and ongoing workflow improvement. The choice is yours; we do not engineer lock-in.",
  },
  {
    q: "How do you handle security, data residency, and compliance while embedded in our environment?",
    a: "Security and compliance are scoped in the readiness sprint, not retrofitted. We document data flows, identify which data categories the AI system touches, and design the controls: role-based access, data residency within your required region, PII segregation and access logging, human-approval points for high-stakes decisions, and audit trails for AI decisions that can be produced for a regulatory or internal review. We have delivered AI systems under financial-services regulation and enterprise security policy, and align to DPDP, RBI, SEBI, and IRDAI requirements where they apply.",
  },
  {
    q: "How does ITMTB price a forward-deployed engagement?",
    a: "Pricing follows the engagement model. The AI Deployment Readiness Sprint is fixed-price for a fixed timebox. The forward-deployed AI sprint is scoped after readiness — fixed-scope, time-and-materials with a budget cap, or outcome-linked. Managed AI Operations is billed monthly against defined SLAs. Total cost depends on scope, integration complexity, evaluation depth, and post-launch support — we publish a transparent breakdown for buyers evaluating their options.",
    link: "/blog/custom-software-development-cost-india",
    linkLabel: "Read the cost guide",
  },
];
forwardDeployedEngineering.faq = faqItems;

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Service",
      name: "Forward Deployed Engineering for Enterprise AI",
      url: "https://www.itmtb.com/services/forward-deployed-engineering",
      image: "https://www.itmtb.com/services/capability/data.webp",
      provider: {
        "@type": "Organization",
        name: "ITMTB Technologies",
        url: "https://www.itmtb.com",
        logo: "https://www.itmtb.com/logo.webp",
        sameAs: [
          "https://www.linkedin.com/company/itmtb-technologies/",
          "https://x.com/ITMTBTech",
          "https://www.instagram.com/itmtbtech/",
        ],
      },
      areaServed: { "@type": "Country", name: "India" },
      serviceType: [
        "Forward Deployed Engineering",
        "Enterprise AI Deployment",
        "Agentic AI Deployment",
        "AI Production Engineering",
        "Fractional Technology Leadership for AI",
        "Managed AI Operations",
      ],
      description:
        "FDE-led deployment: senior engineers embedded with customer teams to take an enterprise AI use case from workflow discovery to secure production, with a handover or managed-operations plan. Not staff augmentation and not a proof of concept.",
      offers: {
        "@type": "Offer",
        priceSpecification: {
          "@type": "PriceSpecification",
          description: "Fixed-scope AI Deployment Readiness Sprint, then a scoped forward-deployed AI sprint based on the use case",
        },
      },
    },
    {
      "@type": "FAQPage",
      mainEntity: faqItems.map(item => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    },
  ],
};

// § What an FDE engagement covers — thematic work areas (Capabilities2)
const includeAreas = [
  {
    img: null,
    key: "Workflow Discovery and Use-Case Definition",
    title: "We observe the real operating process before writing any code — because most AI projects fail on the wrong problem, not the wrong model.",
    text: "We watch how the work actually happens: the users, the decisions, the systems touched, and the exceptions that never make it into a process diagram. From that we define where AI can create measurable value, and we set explicit success criteria and failure boundaries.\n\nThe output is a shared, written definition of the use case that both your team and ours are accountable to — not a vague ambition to 'add AI'.",
  },
  {
    img: null,
    key: "Architecture, Data and Integration",
    title: "Connect the AI to your real systems and data — with identity, permissions, and human-approval points designed in.",
    text: "We connect enterprise systems and data — ERP, CRM, core banking, databases, internal APIs — define identity and permissions, design the agent tools and workflows, and establish where a human must approve before the system acts.\n\nWe also address the cloud, network, and deployment constraints early, so the workflow is designed for the environment it will actually run in rather than a convenient sandbox.",
  },
  {
    img: null,
    key: "Production Engineering and Reliability",
    title: "The difference between a demo and a system: evaluations, audit logs, retries, escalation, and rollback.",
    text: "We build the workflow as production software. That means evaluations and regression tests so quality can be measured and defended, audit logs and observability so every AI decision is traceable, and retries, escalation, and rollback so the system degrades safely when something goes wrong.\n\nReliability in production is an architecture decision made up front — not tuning bolted on after go-live.",
  },
  {
    img: null,
    key: "Adoption and Iteration with Real Users",
    title: "We work alongside the people who use the workflow, and improve it where it actually fails — measured on business results, not demo quality.",
    text: "A workflow that works in testing still meets reality on day one. We stay embedded through adoption: observing where the workflow fails, improving prompts, tools, and process design, and measuring business outcomes rather than model benchmarks.\n\nThis is the phase most vendors skip — and the phase that decides whether an AI system is used or quietly abandoned.",
  },
  {
    img: null,
    key: "Security, Audit and Governance",
    title: "Enterprise AI must meet the same bar as any production system — usually a higher one. We build the controls in from the start.",
    text: "Role-based access, data residency within your required region, PII segregation, full audit trails for AI decisions, and human-in-the-loop escalation for high-stakes actions are scoped during readiness and built in from the first commit.\n\nWhere DPDP, RBI, SEBI, or IRDAI requirements apply, the controls and evidence are designed to satisfy them — producible for a regulatory or internal review.",
  },
  {
    img: null,
    key: "Handover or Continued Operation",
    title: "You choose what happens after go-live: take full ownership, or have us keep operating and improving it.",
    text: "Every engagement is built toward a clean handover — documentation, runbooks, evaluation suites, observability, and team training so your people can own and extend the system.\n\nWhere you would rather not run it in-house, the same team can continue as Managed AI Operations. Either way, ownership is yours; we do not engineer lock-in.",
  },
];

// § How a forward-deployed engagement works — the linear sequence
const engagementPhases = [
  {
    phase: "01",
    title: "AI Deployment Readiness Sprint",
    items: [
      "Observe the real operating process and its exceptions",
      "Define the use case, success criteria, and failure boundaries",
      "Assess data, integration, and security readiness",
      "Produce a target architecture, roadmap, estimate, and risk register",
    ],
  },
  {
    phase: "02",
    title: "Architecture and Integration Design",
    items: [
      "Connect enterprise systems, data, and identity",
      "Design agent tools, workflows, and human-approval points",
      "Resolve cloud, network, and deployment constraints",
      "Agree the evaluation and acceptance criteria",
    ],
  },
  {
    phase: "03",
    title: "Production Build",
    items: [
      "Build the workflow as production software",
      "Add evaluations, regression tests, and observability",
      "Add audit logs, retries, escalation, and rollback",
      "Deploy into the customer-controlled environment",
    ],
  },
  {
    phase: "04",
    title: "Adoption and Iteration",
    items: [
      "Work alongside real users in the live workflow",
      "Observe where it fails and improve prompts, tools, and process",
      "Measure business results against the agreed criteria",
      "Harden the system against real-world inputs",
    ],
  },
  {
    phase: "05",
    title: "Handover or Managed Operations",
    items: [
      "Deliver documentation, runbooks, and evaluation suites",
      "Train your team to own and extend the system",
      "Transfer ownership — or continue as Managed AI Operations",
      "Agree the monitoring, review, and improvement cadence",
    ],
  },
];

// § The service ladder
const engagementModels = [
  {
    label: "AI Deployment Readiness Sprint",
    duration: "~2 weeks · Fixed scope",
    description: "The low-risk entry point. A focused engagement that tells you whether a specific AI use case is worth building — and exactly how.",
    bestFor: [
      "A single AI use case you want to pressure-test",
      "Data and integration readiness assessment",
      "Target architecture and deployment roadmap",
      "Evaluation plan, delivery estimate, and risk register",
      "A go / no-go decision before committing to a build",
    ],
  },
  {
    label: "Forward-Deployed AI Sprint",
    duration: "6 to 12 weeks",
    description: "An embedded pod takes the scoped use case from readiness to a working production system, working alongside your team throughout.",
    bestFor: [
      "One bounded workflow taken to production",
      "Enterprise integrations and human-approval points",
      "Evaluations, audit trails, and observability",
      "Security controls and in-environment deployment",
      "Operating documentation and acceptance evidence",
    ],
  },
  {
    label: "Fractional Technology Leadership",
    duration: "Monthly retainer",
    description: "For organisations facing multiple AI decisions rather than one deployment: senior, vendor-neutral judgment on what to build, govern, and prioritise.",
    bestFor: [
      "AI portfolio priorities and programme oversight",
      "Architecture governance and vendor decisions",
      "AI programme review and technology due diligence",
      "Risk control and executive reporting",
      "Operating-model and build-vs-buy decisions",
    ],
  },
  {
    label: "Managed AI Operations",
    duration: "Monthly · SLA-based",
    description: "After production: keep the system reliable and improving, so you get an asset that stays healthy rather than one you have to babysit.",
    bestFor: [
      "Monitoring, incident handling, and cost tracking",
      "Evaluation regression and output-drift review",
      "Prompt and model updates as the business changes",
      "Ongoing security review",
      "Continuous workflow improvement",
    ],
  },
];

export default function forwardDeployedEngineering() {
  // Lead the "Our Work" rail with AI/agentic case studies where available, then fill with a relevant insight.
  const caseStudies = blogData.filter(item => item?.meta?.category === "case-study");
  const aiCaseStudies = caseStudies.filter(item =>
    item?.meta?.refer?.some(r => ["Data and Artificial Intelligence"].includes(r))
  );
  const leadCases = (aiCaseStudies.length ? aiCaseStudies : caseStudies);
  const pinnedCases = getRandom(leadCases, Math.min(2, leadCases.length));

  const insightPool = blogData.filter(
    item => !["case-study", "tech"].includes(item?.meta?.category)
  );
  const aiInsights = insightPool.filter(i =>
    i?.meta?.refer?.some(r => ["Data and Artificial Intelligence", "Technology Consulting", "Strategy"].includes(r))
  );
  const filler = getRandom(aiInsights.length ? aiInsights : insightPool, 1);

  const insights = [...pinnedCases, ...filler];

  return (
    <div>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      </Head>

      {/* Hero */}
      <Service2
        heading="Forward Deployed Engineering for Enterprise AI"
        paragraph={`Embedded engineers who work alongside your business and technology teams to move a difficult AI use case from workflow discovery to secure production deployment — and keep it running.<br/><br/>This is not an AI strategy deck. It is not a proof of concept that never leaves the sandbox. It is not developers supplied by the hour. A forward-deployed (FDE) engagement owns a defined operational outcome and ends with a system that works in your environment, on your data, for your users.<ul style="margin-top:16px;padding-left:20px;list-style:disc;display:flex;flex-direction:column;gap:8px"><li>Starts with a fixed-scope AI Deployment Readiness Sprint</li><li>A small, senior pod embedded with your team — not a body shop</li><li>Production engineering: evaluations, audit trails, security, and rollback</li><li>Ends with a handover, or continued managed operations — your choice</li></ul>`}
        primaryCta={{ label: "Talk to us →", href: "/contact-us?utm_source=service-fde&utm_content=hero-cta" }}
        heroSvg={
          <svg viewBox="0 0 440 400" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-md opacity-70" aria-hidden="true" role="presentation">
            {/* dot grid */}
            {[...Array(6)].map((_, row) =>
              [...Array(7)].map((_, col) => (
                <circle key={`${row}-${col}`} cx={40 + col * 60} cy={28 + row * 60} r="1.5" fill="#e2e8f0" />
              ))
            )}
            {/* customer environment boundary — the "forward" position */}
            <rect x="150" y="60" width="250" height="290" rx="12" stroke="#94a3b8" strokeWidth="1.5" strokeDasharray="6 4" />
            <text x="164" y="82" fill="#94a3b8" fontSize="11" fontFamily="sans-serif" letterSpacing="1">YOUR ENVIRONMENT</text>
            {/* embedded pod nodes inside the boundary */}
            {[
              [210, 150], [290, 130], [340, 190], [250, 210], [320, 260],
            ].map(([cx, cy], i) => (
              <circle key={i} cx={cx} cy={cy} r="6" stroke="#b45309" strokeWidth="1.5" fill="#fff" fillOpacity="0.2" />
            ))}
            {/* pod interconnections */}
            <path d="M210 150 L290 130 M290 130 L340 190 M340 190 L320 260 M320 260 L250 210 M250 210 L210 150" stroke="#cbd5e1" strokeWidth="1.2" />
            {/* the chain: decide -> deploy -> operate, entering the environment */}
            <line x1="40" y1="120" x2="150" y2="150" stroke="#b45309" strokeWidth="1.4" strokeDasharray="5 3" strokeOpacity="0.6" />
            <line x1="40" y1="200" x2="150" y2="205" stroke="#b45309" strokeWidth="1.4" strokeDasharray="5 3" strokeOpacity="0.6" />
            <line x1="40" y1="280" x2="150" y2="260" stroke="#b45309" strokeWidth="1.4" strokeDasharray="5 3" strokeOpacity="0.6" />
            {[["Decide", 120], ["Deploy", 200], ["Operate", 280]].map(([label, y]) => (
              <g key={label}>
                <circle cx="36" cy={y} r="4" fill="#b45309" fillOpacity="0.5" />
                <text x="12" y={y - 10} fill="#94a3b8" fontSize="11" fontFamily="sans-serif">{label}</text>
              </g>
            ))}
            {/* production checkmark badge */}
            <circle cx="360" cy="320" r="16" stroke="#b45309" strokeWidth="1" strokeOpacity="0.3" strokeDasharray="4 2" />
            <circle cx="360" cy="320" r="10" stroke="#b45309" strokeWidth="1.8" fill="none" strokeOpacity="0.65" />
            <path d="M 355 320 l 3.5 3.5 l 6 -7" stroke="#b45309" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeOpacity="0.7" />
          </svg>
        }
      />

      {/* Anchor nav */}
      <nav className="bg-white border-b border-gray-100 px-6 md:px-[60px] py-2 overflow-x-auto">
        <div className="flex gap-6 text-sm font-medium whitespace-nowrap">
          {[
            { label: "The problem", href: "#the-problem" },
            { label: "Decision to production", href: "#the-chain" },
            { label: "Who it's for", href: "#who-its-for" },
            { label: "What's covered", href: "#whats-covered" },
            { label: "Why embedded", href: "#why-embedded" },
            { label: "Leadership", href: "#leadership" },
            { label: "How it works", href: "#how-it-works" },
            { label: "Engagement models", href: "#engagement-models" },
            { label: "FAQ", href: "#faq" },
          ].map(({ label, href }) => (
            <a key={href} href={href} className="text-gray-500 hover:text-indigo-600 transition-colors py-1">
              {label}
            </a>
          ))}
        </div>
      </nav>

      <CustomerSection title="Trusted by" />

      {/* § The problem */}
      <section id="the-problem" className="scroll-mt-24 px-8 md:px-[60px] py-12 bg-white border-t border-gray-100">
        <div className="max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5">
            Most enterprise AI never reaches production — and the reason is rarely the model
          </h2>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
            The gap between an impressive AI demo and a system people rely on every day is where most enterprise AI initiatives quietly stall.
          </p>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-6">
            A demo works on clean, hand-picked inputs in a controlled setting. Production means real data, real permissions, real exceptions, and real users who will abandon anything that is slower or less trustworthy than what they do today. Crossing that gap is an engineering and adoption problem — not a modelling one.
          </p>
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-5">
            Where enterprise AI initiatives stall
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              "A strategy deck with no path to a running system",
              "A proof of concept that never survives real data",
              "Developers supplied by the hour who own no outcome",
              "No evaluations, so quality can't be measured or defended",
              "No integration with the systems where work happens",
              "No audit trail, so it can't pass a compliance review",
              "No adoption support, so users quietly go back to the old way",
              "No owner after launch, so it degrades and is switched off",
            ].map(sign => (
              <div key={sign} className="flex items-center gap-3 bg-gray-50 border border-gray-100 rounded-lg px-4 py-3">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                <span className="text-sm text-gray-700">{sign}</span>
              </div>
            ))}
          </div>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mt-6">
            Forward deployed engineering is designed to close exactly this gap: the same senior team that scopes the use case builds it, integrates it, deploys it into your environment, and stays alongside your users until it works. We have delivered this in production — a{" "}
            <Link href="/services/analytics-ai-automation">
              <a className="text-indigo-600 hover:text-indigo-800 font-medium underline">99% efficiency gain for a global supply chain firm and automated risk reporting for a global intelligence firm</a>
            </Link>.
          </p>
        </div>
      </section>

      {/* § From decision to production — the chain */}
      <section id="the-chain" className="scroll-mt-24 px-8 md:px-[60px] py-14 bg-gray-50 border-t border-gray-100">
        <div className="max-w-5xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            From technology decision to production operation
          </h2>
          <p className="text-gray-600 text-base md:text-lg mb-10 max-w-3xl">
            Forward deployed engineering is the middle link in a coherent chain. Fractional technology leadership decides what should be built and how it should be governed. Forward-deployed engineers make it operational. Managed services run and improve it after launch. Most engagements use only the layer they need.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                step: "Decide & govern",
                name: "Fractional Technology Leadership",
                body: "Senior judgment on what to build, change, or govern: portfolio priorities, architecture governance, vendor decisions, risk control, and executive reporting. For organisations facing multiple AI decisions rather than one deployment.",
                href: "#engagement-models",
              },
              {
                step: "Deploy",
                name: "Forward Deployed Engineering",
                body: "An embedded pod takes a scoped use case from discovery to a working production system — integrated, evaluated, audited, secured, and adopted by real users.",
                href: "#whats-covered",
                active: true,
              },
              {
                step: "Operate",
                name: "Managed AI Operations",
                body: "Monitoring, incident handling, evaluation regression, cost tracking, model and prompt updates, security review, and continuous improvement after go-live.",
                href: "/services/managed-services",
              },
            ].map(card => (
              <div
                key={card.name}
                className={`rounded-xl p-6 border ${card.active ? "bg-[#402A6A] border-[#402A6A] text-white" : "bg-white border-gray-200"}`}
              >
                <p className={`text-xs font-semibold uppercase tracking-widest mb-3 ${card.active ? "text-amber-300" : "text-indigo-500"}`}>
                  {card.step}
                </p>
                <h3 className={`text-lg font-bold mb-2 ${card.active ? "text-white" : "text-gray-900"}`}>{card.name}</h3>
                <p className={`text-sm leading-relaxed ${card.active ? "text-slate-200" : "text-gray-600"}`}>{card.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* § Who this is for */}
      <section id="who-its-for" className="scroll-mt-24 px-8 md:px-[60px] py-14 bg-white border-t border-gray-100">
        <div className="max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-10">
            Who a forward-deployed engagement is for
          </h2>
          <div className="space-y-10">
            {[
              {
                heading: "Teams with one difficult AI use case that has to reach production",
                body: "You have identified a workflow where AI could create real value — document review, approvals, lookups, report generation, a support agent that reaches into live systems — and you need it running in production, not demonstrated in a meeting. A forward-deployed pod owns that outcome end to end.",
              },
              {
                heading: "Organisations burned by AI proofs of concept that went nowhere",
                body: "If a previous initiative produced an impressive demo that never survived real data, real permissions, or real users, the missing ingredient was production engineering and adoption — not a better model. This model is built specifically to cross that gap.",
              },
              {
                heading: "Enterprises that need AI inside a controlled, compliant environment",
                body: "When data cannot leave your environment and every AI decision must be auditable, you need engineers who design identity, permissions, residency, and audit trails from the first commit — and deploy into your infrastructure, not a third-party sandbox.",
              },
              {
                heading: "Leaders who want an outcome, not a headcount top-up",
                body: "If you have been offered developers by the hour and found yourself still owning all the risk, a forward-deployed engagement is the opposite: a small senior team that owns a defined operational result and stays until it is real.",
              },
              {
                heading: "Organisations facing several AI decisions at once",
                body: "Where the challenge is a portfolio of AI decisions rather than a single deployment, fractional technology leadership sets priorities, governance, and risk control — and the forward-deployed pod executes against that direction.",
              },
            ].map(item => (
              <div key={item.heading}>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.heading}</h3>
                <p className="text-gray-600 text-base leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SuccessStoryEmbed
        referTag="Data and Artificial Intelligence"
        maxItems={3}
        heading="Selected AI work taken to production"
      />

      {/* § What an FDE engagement covers — Capabilities2 */}
      <div id="whats-covered" className="scroll-mt-24">
        <Capabilities2
          dataList={includeAreas}
          bg="bg-[#402A6A]"
          insights={insights}
          hideFooter
          hideCardSubtitle
        />
      </div>

      {/* § Not staff augmentation — credibility */}
      <section className="px-8 md:px-[60px] py-14 bg-white border-t border-gray-100">
        <div className="max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            This is not staff augmentation
          </h2>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
            A forward-deployed engagement owns a defined operational outcome. It works closely with your team and concludes with a production system, evidence, documentation, and a handover or managed-operations plan. You are buying a result and a small senior team that stays close to your users until that result is real — not a headcount top-up billed by the hour.
          </p>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-8">
            We deliver as a small, senior pod — typically a technical lead or architect, one or two engineers, and security or platform input when the deployment requires it — working alongside a workflow specialist from your side. Where an engagement needs to scale, it draws on ITMTB&apos;s wider engineering, cloud, security, and managed-services teams rather than diluting the embedded pod.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">What a forward-deployed engagement is</p>
              <ul className="space-y-2.5">
                {[
                  "Owns a defined operational outcome",
                  "A small, senior, embedded pod",
                  "Ends with a production system and evidence",
                  "Scoped success criteria and failure boundaries",
                  "A clean handover or managed operations",
                ].map(item => (
                  <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">What it is not</p>
              <ul className="space-y-2.5">
                {[
                  "Developers supplied by the hour",
                  "A large bench of interchangeable contractors",
                  "A strategy deck with no path to production",
                  "A proof of concept that stops at the demo",
                  "An engagement that owns no outcome",
                ].map(item => (
                  <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* § Why forward-deployed works — benefits of embedding on-site with business teams */}
      <section id="why-embedded" className="scroll-mt-24 px-8 md:px-[60px] py-14 bg-gray-50 border-t border-gray-100">
        <div className="max-w-5xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            Why working forward-deployed changes the outcome
          </h2>
          <p className="text-gray-600 text-base md:text-lg mb-10 max-w-3xl">
            Where our engineers work — and who they work alongside — is not a logistics detail. It is the difference between a system built for your real operation and one built to a written spec.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "On your site, inside your environment",
                body: "Our engineers deploy where the work actually happens — on your premises or embedded in your environment, not in a distant delivery centre. They see the real process, the real exceptions, and the real systems first-hand, so the workflow is built for reality instead of a brief.",
              },
              {
                title: "Built with your business teams, not handed to them",
                body: "The people who own the workflow sit with the people building it. Tacit knowledge and the exceptions that never make it into a document surface in conversation — not in a change request three sprints later. Because your users co-build the system, they adopt it, which is the single biggest predictor of whether enterprise AI is used or quietly abandoned.",
              },
              {
                title: "Your data never has to leave your perimeter",
                body: "Working inside your environment means sensitive data stays behind your own controls. Deployment happens in infrastructure you own, access is governed by your identity and permissions, and nothing is exfiltrated to an external vendor's cloud. Data-residency and audit obligations — DPDP, RBI, SEBI, IRDAI — are far easier to satisfy when the work never leaves home.",
              },
              {
                title: "Decisions made in the room, not in a ticket queue",
                body: "Embedding compresses the feedback loop. A question that would cost a remote vendor a two-week ticket cycle is answered in a hallway. That proximity is why a scoped use case reaches production in weeks rather than quarters.",
              },
              {
                title: "The capability stays with your team",
                body: "Because your people are alongside the build the whole way, knowledge transfers as the system is created — not in a rushed handover at the end. You are left with a team that understands what was built and can extend it.",
              },
              {
                title: "One team owns the whole path",
                body: "The same people scope, build, deploy, and harden the workflow. There is no handoff from a strategy team to a build team to an ops team — and none of the meaning that gets lost at each of those boundaries.",
              },
            ].map(b => (
              <div key={b.title} className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="text-base font-bold text-gray-900 mb-2">{b.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* § Fractional Technology Leadership — the decide & govern layer, elaborated */}
      <section id="leadership" className="scroll-mt-24 px-8 md:px-[60px] py-14 bg-white border-t border-gray-100">
        <div className="max-w-5xl">
          <p className="text-xs font-semibold uppercase tracking-widest text-indigo-500 mb-3">Decide &amp; govern</p>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            Fractional Technology Leadership: senior judgment for consequential AI decisions
          </h2>
          <p className="text-gray-600 text-base md:text-lg mb-10 max-w-3xl">
            When the challenge is not one deployment but a series of decisions — what to fund, what to govern, which vendor to back, what risk you are quietly carrying — you need senior technology judgment that is accountable for the outcome. Fractional technology leadership provides it without a full-time executive hire.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6 mb-12">
            {[
              {
                title: "CTO-level judgment, sized to the decisions in front of you",
                body: "Senior technology leadership on a retainer scaled to what you actually face — not the fixed cost of a permanent executive you may not yet need.",
              },
              {
                title: "Independent and vendor-neutral",
                body: "An assessment with no product to sell and no incentive to over-build. You get the recommendation your own interests point to, not a platform someone is trying to place.",
              },
              {
                title: "One person accountable for the AI portfolio",
                body: "Priorities, architecture governance, and risk control owned by a single senior person — so decisions stop falling between teams and vendors.",
              },
              {
                title: "Board-ready translation",
                body: "Technical reality rendered into the risk, cost, and timeline language executives and investors can act on — closing the gap between board ambition and engineering reality.",
              },
              {
                title: "Governance and risk built for AI specifically",
                body: "Model risk, data governance, human-approval design, and audit posture handled by someone who has actually shipped production AI, not just advised on it.",
              },
              {
                title: "Judgment that can be executed",
                body: "Because the same firm can deploy, a recommendation does not die as a slide. The forward-deployed pod can turn the decision into a running system.",
              },
            ].map(b => (
              <div key={b.title} className="flex gap-3">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                <div>
                  <h3 className="text-base font-bold text-gray-900 mb-1">{b.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{b.body}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">Engagement forms</p>
          <div className="flex flex-wrap gap-2.5">
            {[
              "Fractional CTO",
              "Interim CTO",
              "Independent architecture review",
              "AI programme review",
              "Technology due diligence",
              "Delivery recovery mandate",
            ].map(form => (
              <span key={form} className="inline-block bg-gray-50 border border-gray-200 rounded-full px-4 py-2 text-sm text-gray-700">
                {form}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* § How it works */}
      <section id="how-it-works" className="scroll-mt-24 px-8 md:px-[60px] py-14 bg-gray-50 border-t border-gray-100">
        <div className="max-w-5xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            How a forward-deployed engagement works
          </h2>
          <p className="text-gray-600 text-base md:text-lg mb-10">
            A defined five-phase path from a scoped use case to a system your team owns — starting with a low-risk, fixed-scope sprint.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {engagementPhases.map(phase => (
              <div key={phase.phase} className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="flex items-start gap-4 mb-5">
                  <span className="text-3xl font-bold text-indigo-200 leading-none select-none">
                    {phase.phase}
                  </span>
                  <div>
                    <h3 className="text-base font-bold text-gray-900">{phase.title}</h3>
                  </div>
                </div>
                <ul className="space-y-2.5">
                  {phase.items.map(item => (
                    <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <Link href="/itmtb-how-we-work">
            <a className="mt-8 inline-flex items-center text-sm font-semibold text-indigo-600 hover:text-indigo-800 transition-colors">
              See the full engagement methodology →
            </a>
          </Link>
        </div>
      </section>

      {/* § Engagement models — the ladder */}
      <section id="engagement-models" className="scroll-mt-24 px-8 md:px-[60px] py-14 bg-white border-t border-gray-100">
        <div className="max-w-6xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            A service ladder from first decision to sustained operation
          </h2>
          <p className="text-gray-600 text-base md:text-lg mb-10">
            Start with a fixed-scope readiness sprint, then engage only the layers you need — deployment, leadership, ongoing operations, or all three.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {engagementModels.map(model => (
              <div key={model.label} className="bg-gray-50 border border-gray-200 rounded-xl p-6 flex flex-col">
                <div className="mb-4">
                  <h3 className="text-base font-bold text-gray-900 mb-1">{model.label}</h3>
                  <p className="text-xs font-semibold text-indigo-500 uppercase tracking-wide">{model.duration}</p>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed mb-5">{model.description}</p>
                <div className="mt-auto">
                  <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3">Best for</p>
                  <ul className="space-y-2">
                    {model.bestFor.map(item => (
                      <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* § Related capabilities */}
      <section className="px-8 md:px-[60px] py-14 bg-gray-50 border-t border-gray-100">
        <div className="max-w-5xl">
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-7">
            Related capabilities
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                href: "/services/analytics-ai-automation",
                label: "AI Agents & Automation",
                desc: "The capability behind the deployment: production AI agents that run enterprise workflows end to end, built and operated by ITMTB.",
              },
              {
                href: "/agentic-ai",
                label: "Agentic AI Services",
                desc: "What we build — production multi-agent systems for enterprise workflows, with orchestration, tools, and human-approval points.",
              },
              {
                href: "/services/technology-consulting",
                label: "Technology Consulting",
                desc: "Independent technical due diligence, architecture review, and CTO-level advisory for organisations making major technology decisions.",
              },
              {
                href: "/services/managed-services",
                label: "Managed Services",
                desc: "The operate layer: monitoring, incident handling, and continuous improvement for AI systems after they go live.",
              },
              {
                href: "/ai-strategy-for-business",
                label: "AI Strategy for Business",
                desc: "How enterprises should set AI strategy and prioritise the high-value use cases worth deploying first.",
              },
              {
                href: "/services/cybersecurity",
                label: "Cybersecurity",
                desc: "Security controls, access governance, and audit readiness for AI systems handling sensitive enterprise data.",
              },
            ].map(link => (
              <Link key={link.href} href={link.href}>
                <a className="block bg-white border border-gray-200 rounded-xl p-6 hover:border-indigo-400 hover:shadow-sm transition-all group">
                  <h3 className="text-base font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors mb-2">
                    {link.label}
                  </h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{link.desc}</p>
                  <span className="mt-4 inline-block text-sm font-semibold text-indigo-500 group-hover:text-indigo-700 transition-colors">
                    Learn more →
                  </span>
                </a>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <div id="faq" className="scroll-mt-24">
        <ServiceFAQ items={faqItems} />
      </div>

      <ServiceEngagementLinks />

      {/* Final CTA */}
      <section className="px-8 md:px-[60px] py-16 bg-[#0d0822]">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-indigo-400 mb-5">
            Start the conversation
          </p>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-5">
            Have an AI use case that needs to reach production?
          </h2>
          <p className="text-slate-300 text-base md:text-lg leading-relaxed mb-8">
            Start with a fixed-scope AI Deployment Readiness Sprint. In about two weeks you will know whether the use case is worth building, what it will take, and how it will be deployed securely into your environment — before you commit to a build.
          </p>
          <Link href="/contact-us?utm_source=service-fde&utm_content=bottom-cta">
            <a className="inline-block bg-[#f1aa4e] text-black font-semibold text-base px-8 py-3.5 rounded-md hover:bg-[#e09a3e] transition-colors">
              Request a Readiness Sprint →
            </a>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
